import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, PageHeader, Button, Field, Input, Select, Modal } from "@/components/ui-kit";
import FilePreview from "@/components/FilePreview";
import { ScrollText, Plus, Trash2, Upload, Eye, FileText, Camera, Pencil, X, Filter } from "lucide-react";
import { formatNumber, parseQty, todayISO } from "@/lib/format";
import { DatePicker } from "@/components/DatePicker";

// Iter v34br (Task 2) — Plat mobil + Vendor (Master Vendor).
const blankForm = { material_id: "", type: "in", qty_value: 0, qty_unit: "kg", date: todayISO(), ref: "", note: "", proofs: [], plat_mobil: "", vendor_id: "", vendor_name: "" };

export default function BatchingInputMaterial() {
  const [materials, setMaterials] = useState([]);
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  // Iter v34bd — Filter tanggal untuk riwayat.
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  // Iter v34bd — Edit mode.
  const [editingId, setEditingId] = useState(null);
  // Iter v34br (Task 2) — Modal pop-out untuk input entry + master vendor.
  const [modalOpen, setModalOpen] = useState(false);
  const [vendors, setVendors] = useState([]);

  const loadMaterials = () => api.get("/batching/materials").then((r) => setMaterials(r.data || []));
  const loadVendors = () => api.get("/vendors", { params: { active_only: true } }).then((r) => setVendors(r.data || [])).catch(() => setVendors([]));
  const loadEntries = () => {
    const params = {};
    if (filterFrom) params.date_from = filterFrom;
    if (filterTo) params.date_to = filterTo;
    return api.get("/batching/stock-entries", { params }).then((r) => setEntries(r.data || []));
  };
  useEffect(() => { loadMaterials(); loadVendors(); }, []);
  useEffect(() => { loadEntries(); }, [filterFrom, filterTo]);  // eslint-disable-line react-hooks/exhaustive-deps

  const matMap = useMemo(() => Object.fromEntries(materials.map((m) => [m.id, m])), [materials]);
  const previewKg = useMemo(() => {
    const v = parseQty(form.qty_value);
    if ((form.qty_unit || "kg").toLowerCase() === "kg") return v;
    const bj = parseQty(matMap[form.material_id]?.bj_kg_per_m3 || 1500);
    return v * bj;
  }, [form.qty_value, form.qty_unit, form.material_id, matMap]);

  // Iter v34cz (#3) — Filter per material + ringkasan total masuk/keluar.
  const [filterMaterial, setFilterMaterial] = useState("");
  const visibleEntries = useMemo(
    () => entries.filter((e) => matMap[e.material_id] && (!filterMaterial || e.material_id === filterMaterial)),
    [entries, filterMaterial, matMap],
  );
  const totals = useMemo(() => {
    let inKg = 0, outKg = 0, adjKg = 0;
    for (const e of visibleEntries) {
      // Abaikan entry milik material yang sudah dihapus agar total tidak palsu.
      if (!matMap[e.material_id]) continue;
      const v = Number(e.qty_kg) || 0;
      if (e.type === "in") inKg += v;
      else if (e.type === "adjustment") adjKg += v;
      else outKg += v;
    }
    return { inKg, outKg, adjKg, net: inKg - outKg - adjKg };
  }, [visibleEntries, matMap]);
  const totalsM3 = useMemo(() => {
    const bj = parseQty(matMap[filterMaterial]?.bj_kg_per_m3 || 0);
    if (!filterMaterial || !bj) return null;
    return { inM3: totals.inKg / bj, outM3: totals.outKg / bj, netM3: totals.net / bj };
  }, [filterMaterial, matMap, totals]);

  const resetForm = () => { setForm(blankForm); setEditingId(null); setModalOpen(false); };

  // Iter v34br (Task 2) — Buka Modal untuk create new entry.
  const openNew = () => { setForm(blankForm); setEditingId(null); setModalOpen(true); };

  const submit = async () => {
    if (!form.material_id) { toast.error("Pilih material dulu"); return; }
    setSaving(true);
    try {
      const payload = { ...form, qty_value: parseQty(form.qty_value) };
      if (editingId) {
        await api.patch(`/batching/stock-entries/${editingId}`, payload);
        toast.success("Perubahan tersimpan");
      } else {
        await api.post("/batching/stock-entries", payload);
        toast.success(`Tercatat: ${formatNumber(previewKg, 3)} kg`);
      }
      resetForm();
      loadEntries();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };

  const uploadProof = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file); fd.append("kind", "batching_stock");
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: r.data.id, path: r.data.path, filename: r.data.filename, content_type: r.data.content_type });
      }
      setForm((f) => ({ ...f, proofs: [...(f.proofs || []), ...uploaded] }));
      toast.success(files.length > 1 ? `${files.length} bukti terunggah` : "Bukti terunggah");
    } catch { toast.error("Gagal upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const rmProof = (idx) => setForm((f) => ({ ...f, proofs: (f.proofs || []).filter((_, i) => i !== idx) }));

  const del = async (id) => {
    if (!window.confirm("Hapus entry ini?")) return;
    try { await api.delete(`/batching/stock-entries/${id}`); loadEntries(); } catch { toast.error("Gagal"); }
  };

  // Iter v34bd — Load entry ke form untuk edit.
  const startEdit = (entry) => {
    setEditingId(entry.id);
    setForm({
      material_id: entry.material_id || "",
      type: entry.type || "in",
      qty_value: entry.qty_value || 0,
      qty_unit: entry.qty_unit || "kg",
      date: entry.date || todayISO(),
      ref: entry.ref || "",
      note: entry.note || "",
      proofs: entry.proofs || [],
      // Iter v34br (Task 2)
      plat_mobil: entry.plat_mobil || "",
      vendor_id: entry.vendor_id || "",
      vendor_name: entry.vendor_name || "",
    });
    setModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        overline="Batching Plant"
        title="Input Material"
        description="Catat material masuk / keluar. Konversi otomatis m³ ↔ kg memakai Berat Jenis (BJ) di Master Material."
        icon={ScrollText}
        action={materials.length > 0 && (
          <Button onClick={openNew} data-testid="batching-entry-new-btn">
            <Plus className="h-4 w-4" /> Input Material Baru
          </Button>
        )}
      />

      {materials.length === 0 && (
        <Card className="p-6 text-center text-sm text-stone-500">
          Belum ada master material. Tambah dulu di halaman <a href="/batching/stock" className="text-terra-600 underline">Stok Material</a>.
        </Card>
      )}

      {/* Iter v34br (Task 2) — Form input pindah ke Modal pop-out. */}
      <Modal open={modalOpen} onClose={resetForm} title={editingId ? "Edit Entry Stok" : "Input Material Baru"} size="lg">
        <div className="space-y-3">
          {editingId && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 px-3 py-2 text-xs">
              <span className="text-blue-800"><b>Mode Edit</b> · Entry ID: <code className="font-mono">{editingId.slice(0, 8)}</code></span>
              <button onClick={resetForm} className="text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"><X className="h-3 w-3" /> Batal Edit</button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Field label="Tipe"><Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="batching-entry-type"><option value="in">Masuk (In)</option><option value="out">Keluar (Out)</option></Select></Field>
            <Field label="Material"><Select value={form.material_id} onChange={(e) => setForm({ ...form, material_id: e.target.value })} data-testid="batching-entry-material">
              <option value="">— Pilih —</option>
              {materials.map((m) => <option key={m.id} value={m.id}>{m.name} (BJ {formatNumber(m.bj_kg_per_m3)})</option>)}
            </Select></Field>
            <Field label="Tanggal"><DatePicker value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label="Jumlah"><Input className="text-right font-mono-num" value={form.qty_value} onChange={(e) => setForm({ ...form, qty_value: e.target.value })} data-testid="batching-entry-qty" /></Field>
            <Field label="Satuan"><Select value={form.qty_unit} onChange={(e) => setForm({ ...form, qty_unit: e.target.value })} data-testid="batching-entry-unit"><option value="kg">kg</option><option value="m3">m³</option></Select></Field>
            <Field label="No. Ref (PO/SJ)"><Input value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} placeholder="PO-RBB-..." /></Field>
            {/* Iter v34br (Task 2) — Plat Mobil + Vendor (untuk material MASUK dari supplier). */}
            {form.type === "in" && (
              <>
                <Field label="Plat Mobil Pengantar" hint="Text bebas — plat kendaraan supplier">
                  <Input value={form.plat_mobil} onChange={(e) => setForm({ ...form, plat_mobil: e.target.value })} placeholder="mis: BG 1234 XY" data-testid="batching-entry-plat" />
                </Field>
                <Field label="Vendor Pengirim" hint="Dari Master Vendor (Vendor & AP)">
                  <Select
                    value={form.vendor_id}
                    onChange={(e) => {
                      const v = vendors.find((x) => x.id === e.target.value);
                      setForm({ ...form, vendor_id: e.target.value, vendor_name: v ? v.name : "" });
                    }}
                    data-testid="batching-entry-vendor"
                  >
                    <option value="">— Pilih Vendor —</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    {form.vendor_id && !vendors.some((v) => v.id === form.vendor_id) && (
                      <option value={form.vendor_id}>{form.vendor_name || "(vendor lama)"}</option>
                    )}
                  </Select>
                </Field>
                <Field label=""><span className="text-[10px] text-stone-500 italic">Vendor tidak ada? Tambahkan di menu Vendor & AP.</span></Field>
              </>
            )}
          </div>
          <div className="flex items-center justify-between bg-stone-50 px-3 py-2 text-xs">
            <span className="text-stone-600">Konversi otomatis → <b className="font-mono">{formatNumber(previewKg, 3)} kg</b></span>
            <Button onClick={submit} disabled={saving} data-testid="batching-entry-save">
              <Plus className="h-3.5 w-3.5" /> {saving ? "Menyimpan…" : (editingId ? "Simpan Perubahan" : "Catat")}
            </Button>
          </div>
          <div className="border-t border-stone-200 pt-3">
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-stone-600 mb-2">Bukti Surat Jalan / Dokumen (opsional)</label>
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 rounded cursor-pointer hover:bg-stone-50 text-sm">
                <Upload className="h-4 w-4" />{uploading ? "Mengunggah…" : ((form.proofs || []).length ? "Tambah dari file" : "Pilih dari file")}
                <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf" onChange={uploadProof} className="hidden" data-testid="batching-entry-proof-input" />
              </label>
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-terra-300 bg-terra-50 rounded cursor-pointer hover:bg-terra-100 text-sm text-terra-800">
                <Camera className="h-4 w-4" />Foto Langsung
                <input type="file" accept="image/*" capture="environment" onChange={uploadProof} className="hidden" data-testid="batching-entry-camera-input" />
              </label>
            </div>
            {(form.proofs || []).length > 0 && (
              <div className="mt-2 space-y-1 border border-stone-200 rounded p-2 bg-stone-50/50">
                {(form.proofs || []).map((pf, idx) => (
                  <div key={pf.id || idx} className="flex items-center gap-2 text-xs">
                    <FileText className="h-3.5 w-3.5 text-stone-500 shrink-0" />
                    <span className="text-emerald-800 truncate flex-1">{pf.filename || "(tanpa nama)"}</span>
                    <button type="button" onClick={() => setPreviewFile({ id: pf.id, name: pf.filename, ct: pf.content_type })}
                      className="text-terra-600 hover:text-terra-700 inline-flex items-center gap-1 shrink-0">
                      <Eye className="h-3.5 w-3.5" />Preview
                    </button>
                    <button type="button" onClick={() => rmProof(idx)} className="text-stone-500 hover:text-red-700 shrink-0">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Iter v34bd — Filter tanggal riwayat */}
      <Card className="p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex items-center gap-1 text-xs font-semibold uppercase text-stone-600 mr-2">
            <Filter className="h-3.5 w-3.5" /> Filter Riwayat
          </div>
          <Field label="Dari Tanggal"><DatePicker value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} data-testid="batching-entry-filter-from" /></Field>
          <Field label="Sampai Tanggal"><DatePicker value={filterTo} onChange={(e) => setFilterTo(e.target.value)} data-testid="batching-entry-filter-to" /></Field>
          <Field label="Material">
            <Select value={filterMaterial} onChange={(e) => setFilterMaterial(e.target.value)} data-testid="batching-entry-filter-material">
              <option value="">— Semua Material —</option>
              {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </Select>
          </Field>
          {(filterFrom || filterTo || filterMaterial) && (
            <Button variant="ghost" onClick={() => { setFilterFrom(""); setFilterTo(""); setFilterMaterial(""); }} data-testid="batching-entry-filter-reset">
              <X className="h-3.5 w-3.5" /> Reset
            </Button>
          )}
          <div className="ml-auto text-xs text-stone-500">Total: {visibleEntries.length} entry</div>
        </div>
      </Card>

      {/* Iter v34cz (#3) — Kartu ringkasan total masuk / keluar / selisih */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="batching-entry-summary">
        <Card className="p-4 border-l-4 border-l-emerald-500">
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Total Masuk</div>
          <div className="font-mono-num text-xl font-bold text-emerald-700" data-testid="summary-total-in">{formatNumber(totals.inKg, 2)} kg</div>
          {totalsM3 && <div className="text-[11px] text-stone-500 font-mono-num">≈ {formatNumber(totalsM3.inM3, 3)} m³</div>}
        </Card>
        <Card className="p-4 border-l-4 border-l-red-500">
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Total Keluar{totals.adjKg ? " + Penyesuaian" : ""}</div>
          <div className="font-mono-num text-xl font-bold text-red-700" data-testid="summary-total-out">{formatNumber(totals.outKg + totals.adjKg, 2)} kg</div>
          {totalsM3 && <div className="text-[11px] text-stone-500 font-mono-num">≈ {formatNumber(totalsM3.outM3, 3)} m³</div>}
        </Card>
        <Card className="p-4 border-l-4 border-l-stone-800">
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Selisih (Masuk − Keluar)</div>
          <div className="font-mono-num text-xl font-bold" data-testid="summary-net">{formatNumber(totals.net, 2)} kg</div>
          {totalsM3 && <div className="text-[11px] text-stone-500 font-mono-num">≈ {formatNumber(totalsM3.netM3, 3)} m³</div>}
        </Card>
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-600">
            <tr><th className="text-left py-2 px-3">Tanggal</th><th className="text-left py-2 px-3">Material</th><th className="text-center py-2 px-3">Tipe</th><th className="text-right py-2 px-3">Jumlah</th><th className="text-right py-2 px-3">→ kg</th><th className="text-left py-2 px-3">Ref</th><th className="text-center py-2 px-3">Bukti</th><th className="text-right py-2 px-3 w-24">Aksi</th></tr>
          </thead>
          <tbody>
            {visibleEntries.length === 0 && <tr><td colSpan={8} className="text-center text-stone-400 py-8 text-xs">Belum ada entry.</td></tr>}
            {visibleEntries.map((e) => (
              <tr key={e.id} className={`border-b border-stone-100 ${editingId === e.id ? "bg-blue-50/50" : ""}`}>
                <td className="py-2 px-3 font-mono">{e.date}</td>
                <td className="py-2 px-3">{matMap[e.material_id]?.name || "(deleted)"}</td>
                <td className="py-2 px-3 text-center"><span className={e.type === "in" ? "text-emerald-700" : "text-red-700"}>{e.type === "in" ? "MASUK" : "KELUAR"}</span></td>
                <td className="py-2 px-3 text-right font-mono">{formatNumber(e.qty_value, 3)} {e.qty_unit}</td>
                <td className="py-2 px-3 text-right font-mono">{formatNumber(e.qty_kg, 3)}</td>
                <td className="py-2 px-3 text-stone-600">{e.ref || "-"}</td>
                <td className="py-2 px-3 text-center">
                  {(e.proofs || []).length > 0 ? (
                    <div className="flex justify-center gap-1">
                      {(e.proofs || []).map((pf, pi) => (
                        <button key={pf.id || pi} onClick={() => setPreviewFile({ id: pf.id, name: pf.filename, ct: pf.content_type })}
                          className="inline-flex items-center gap-0.5 text-blue-700 hover:bg-blue-50 border border-stone-200 px-1 py-0.5 text-[10px] rounded"
                          title={pf.filename}>
                          <Eye className="h-3 w-3" />{pi + 1}
                        </button>
                      ))}
                    </div>
                  ) : <span className="text-stone-300">—</span>}
                </td>
                <td className="py-2 px-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => startEdit(e)} className="text-stone-500 hover:text-blue-700" title="Edit" data-testid={`batching-entry-edit-${e.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del(e.id)} className="text-stone-500 hover:text-red-700" title="Hapus" data-testid={`batching-entry-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <FilePreview open={!!previewFile} onClose={() => setPreviewFile(null)} fileId={previewFile?.id} filename={previewFile?.name} contentType={previewFile?.ct} />
    </div>
  );
}
